/**
 * S5 chart #4 (part 2) — the candidate influence-event timeline. Granger-style
 * lead-lag findings across the agenda ladder (public → media → elite): does a
 * term's attention in a lower agenda PREDICT a rise in a higher one?
 *
 * Causal humility is the whole point (plan §1.2, Granger 2004): every finding is
 * a CANDIDATE, never an assertion of causation. Media series reach back to 2019
 * (S7 backfill); pairs whose public/elite leg is still shallow are flagged
 * `underpowered`. We surface them anyway, clearly labelled, so the instrument
 * is visible.
 */
import type { SalienceInfluence } from "../../lib/api";
import { SALIENCE_AGENDA_LABELS } from "../../lib/api";

interface InfluenceCandidatesProps {
  items: SalienceInfluence[];
  linkTerms?: boolean;
  termLabels?: Record<string, string>;
  emptyMessage?: string;
}

export function InfluenceCandidates({
  items,
  termLabels = {},
  emptyMessage = "No lead-lag pairs computed yet.",
}: InfluenceCandidatesProps) {
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-white/5 bg-terminal-panel/40 p-5 text-sm text-terminal-ink-muted">
        {emptyMessage}
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <InfluenceRow
          key={`${it.termId}-${it.leaderAgenda}-${it.followerAgenda}-${i}`}
          item={it}
          termLabel={termLabels[it.termId] ?? it.termId}
        />
      ))}
    </ul>
  );
}

function InfluenceRow({
  item,
  termLabel,
}: {
  item: SalienceInfluence;
  termLabel: string;
}) {
  const r = item.correlation;
  const p = item.grangerP;
  return (
    <li className="rounded-lg border border-white/10 bg-terminal-panel p-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-terminal-ink">
            {termLabel}
          </span>
          <span className="flex items-center gap-1.5 font-mono text-xs text-terminal-ink-muted">
            <span className="rounded bg-agenda-public/20 px-1.5 py-0.5 text-[#7da6ff]">
              {SALIENCE_AGENDA_LABELS[item.leaderAgenda]}
            </span>
            <span aria-hidden>→</span>
            <span className="rounded bg-agenda-media/20 px-1.5 py-0.5 text-[#d2a8fc]">
              {SALIENCE_AGENDA_LABELS[item.followerAgenda]}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {item.candidate ? (
            <span className="rounded bg-up/15 px-1.5 py-0.5 text-[11px] font-semibold text-up">
              Candidate
            </span>
          ) : (
            <span className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] font-semibold text-terminal-ink-muted">
              No signal
            </span>
          )}
          {item.underpowered && (
            <span className="rounded bg-[#f5a524]/15 px-1.5 py-0.5 text-[11px] font-semibold text-[#f5a524]">
              Underpowered
            </span>
          )}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs tabular-nums text-terminal-ink-muted sm:grid-cols-4">
        <Stat label="Lead" value={item.bestLagDays != null ? `${item.bestLagDays}d` : "—"} />
        <Stat label="Corr r" value={r != null ? r.toFixed(2) : "—"} />
        <Stat label="Granger p" value={p != null ? p.toFixed(3) : "—"} />
        <Stat label="n days" value={item.nOverlap != null ? String(item.nOverlap) : "—"} />
      </div>

      <p className="mt-2 text-[11px] leading-snug text-terminal-ink-muted">
        {item.caveat}
      </p>
    </li>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 sm:flex-col sm:items-start sm:justify-start sm:gap-0">
      <span className="text-[10px] uppercase tracking-wider text-terminal-ink-muted/70">
        {label}
      </span>
      <span className="font-semibold text-terminal-ink">{value}</span>
    </div>
  );
}
